import React, { useState, useEffect } from 'react';
import { getAuditLogs, exportAuditLogs, getAuditLogStats } from '../../../services/auditLogService';
import Icon from '../../../components/AppIcon';
import toast from 'react-hot-toast';

const AuditLogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    actionType: '',
    severity: '',
    startDate: '',
    endDate: '',
    userId: ''
  });
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0
  });

  useEffect(() => {
    loadAuditLogs();
    loadStats();
  }, [filters, pagination?.offset]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const result = await getAuditLogs({
        ...filters,
        limit: pagination?.limit,
        offset: pagination?.offset
      });

      if (result?.success) {
        setLogs(result?.data);
        setPagination(prev => ({ ...prev, total: result?.total }));
      } else {
        toast?.error('Failed to load audit logs');
      }
    } catch (error) {
      console.error('Load audit logs error:', error);
      toast?.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await getAuditLogStats();
      if (result?.success) {
        setStats(result?.data);
      }
    } catch (error) {
      console.error('Load stats error:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const result = await exportAuditLogs(filters);
      if (result?.success) {
        toast?.success('Audit logs exported successfully');
      } else {
        toast?.error('Failed to export audit logs');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast?.error('Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'text-destructive bg-destructive/10';
      case 'warning':
        return 'text-warning bg-warning/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const formatActionType = (actionType) => {
    if (!actionType) return '';
    return actionType
      .replace(/\./g, ' › ')
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name="Activity" size={20} className="text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Events (30d)</p>
                <p className="text-2xl font-bold text-foreground">{stats?.totalEvents}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-warning/10 rounded-lg">
                <Icon name="AlertTriangle" size={20} className="text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Warnings</p>
                <p className="text-2xl font-bold text-foreground">{stats?.bySeverity?.warning || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-destructive/10 rounded-lg">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Critical</p>
                <p className="text-2xl font-bold text-foreground">{stats?.bySeverity?.critical || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Action Type</label>
            <select
              value={filters?.actionType}
              onChange={(e) => handleFilterChange('actionType', e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Actions</option>
              <optgroup label="Auth">
                <option value="login">Login</option>
                <option value="logout">Logout</option>
                <option value="2fa_setup">2FA Setup</option>
                <option value="2fa_disable">2FA Disable</option>
              </optgroup>
              <optgroup label="Cases">
                <option value="case_create">Case Create</option>
                <option value="case_export">Case Export</option>
                <option value="report_download">Report Download</option>
              </optgroup>
              <optgroup label="Admin">
                <option value="admin_action">Admin Action</option>
                <option value="expert_approval">Expert Approval</option>
              </optgroup>
              <optgroup label="Contract">
                <option value="contract.">All Contract Events</option>
                <option value="contract.created">Contract Created</option>
                <option value="contract.accepted">Contract Accepted</option>
                <option value="contract.started">Contract Started</option>
                <option value="contract.submitted_for_review">Submitted for Review</option>
                <option value="contract.completed">Contract Completed</option>
                <option value="contract.cancelled">Contract Cancelled</option>
                <option value="contract.milestone_submitted">Milestone Submitted</option>
                <option value="contract.milestone_approved">Milestone Approved</option>
                <option value="contract.milestone_rejected">Milestone Rejected</option>
                <option value="contract.time_entry_approved">Time Entry Approved</option>
                <option value="contract.time_entry_rejected">Time Entry Rejected</option>
              </optgroup>
              <optgroup label="Payment">
                <option value="payment.">All Payment Events</option>
                <option value="payment.escrow_held">Escrow Held</option>
                <option value="payment.escrow_released">Escrow Released</option>
                <option value="payment.escrow_refunded">Escrow Refunded</option>
                <option value="payment.connect_account_created">Connect Account Created</option>
                <option value="payment.withdrawal_initiated">Withdrawal Initiated</option>
                <option value="payment.credit_consumed">Credit Consumed</option>
                <option value="payment.credits_added">Credits Added</option>
                <option value="payment.subscription_cancelled">Subscription Cancelled</option>
                <option value="payment.subscription_reactivated">Subscription Reactivated</option>
                <option value="payment.subscription_plan_changed">Plan Changed</option>
                <option value="payment.method_removed">Payment Method Removed</option>
                <option value="payment_event">Payment Event (legacy)</option>
                <option value="payout_process">Payout Process (legacy)</option>
              </optgroup>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Severity</label>
            <select
              value={filters?.severity}
              onChange={(e) => handleFilterChange('severity', e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Severities</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Start Date</label>
            <input
              type="date"
              value={filters?.startDate}
              onChange={(e) => handleFilterChange('startDate', e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">End Date</label>
            <input
              type="date"
              value={filters?.endDate}
              onChange={(e) => handleFilterChange('endDate', e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Icon name="Download" size={16} />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>
        </div>
      </div>
      {/* Logs Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">IP Address</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Severity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    <Icon name="Loader2" size={24} className="animate-spin mx-auto mb-2" />
                    Loading audit logs...
                  </td>
                </tr>
              ) : logs?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    No audit logs found
                  </td>
                </tr>
              ) : (
                logs?.map((log) => (
                  <tr key={log?.id} className="hover:bg-muted/30 transition-smooth">
                    <td className="px-4 py-3 text-sm text-foreground whitespace-nowrap">
                      {new Date(log?.created_at)?.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {log?.actor_email || 'System'}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {formatActionType(log?.action_type)}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground max-w-xs truncate">
                      {log?.action_description || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {log?.actor_ip_address || 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(log?.severity)}`}>
                        {log?.severity}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination?.total > pagination?.limit && (
          <div className="px-4 py-3 border-t border-border flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {pagination?.offset + 1} to {Math.min(pagination?.offset + pagination?.limit, pagination?.total)} of {pagination?.total} logs
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev?.offset - prev?.limit) }))}
                disabled={pagination?.offset === 0}
                className="px-3 py-1 bg-background border border-border rounded text-sm text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: prev?.offset + prev?.limit }))}
                disabled={pagination?.offset + pagination?.limit >= pagination?.total}
                className="px-3 py-1 bg-background border border-border rounded text-sm text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogViewer;